import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardDrive, KeyRound, RefreshCw, ShieldOff, WifiOff } from 'lucide-react';
import type {
  OwnerDownloadPinResponseDto,
  OwnerIssueEnrollmentTokenResponseDto,
  OwnerTenantDeploymentDto,
} from '@his/shared';
import { api, get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Spinner, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';
import { TenantCommandsCard } from './TenantCommandsCard';
import { TenantComparePanel } from './TenantComparePanel';
import { LicenceDeliveryPill } from './LicenceDeliveryPill';
import { TenantGraceCard } from './TenantGraceCard';
import { ReplaceServerDialog } from './ReplaceServerDialog';
import { useOwnerCapability } from './owner-nav';

export const LOCK_STATE_BADGE: Record<
  OwnerTenantDeploymentDto['lockState'],
  { variant: 'success' | 'warning' | 'destructive'; label: string }
> = {
  NONE: { variant: 'success', label: 'Writable' },
  STALE: { variant: 'warning', label: 'Read-only (stale)' },
  MISMATCH: { variant: 'destructive', label: 'Read-only (machine mismatch)' },
  VERSION: { variant: 'destructive', label: 'Read-only (update required)' },
};

const ACTIVATION_LABEL: Record<OwnerTenantDeploymentDto['activationState'], string> = {
  NOT_ACTIVATED: 'Not activated',
  ACTIVE: 'Activated',
  MISMATCH: 'Fingerprint mismatch',
  RETIRED: 'Retired',
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function SecretBox({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-success/30 bg-success/5 p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 select-all break-all font-mono text-sm">{value}</p>
      {hint && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/**
 * Customers → Deployment tab (docs/SYNC.md; plan §3.6): where this hospital
 * writes, its local install's activation and health, the grace period
 * before an offline install goes read-only, and the four owner actions —
 * issue an activation token, regenerate the download PIN, retire the
 * fingerprint, force "update required" — plus the per-hospital "Allow
 * offline mode" switch (default off) that gates the token, the PIN and the
 * installer download. Every action asks for the operator's live
 * authenticator code, like the rest of the console.
 */
export function TenantDeploymentPanel({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const key = ['tenant-deployment', tenantId];
  const base = `/owner/tenants/${tenantId}`;

  const [totpCode, setTotpCode] = useState('');
  const [graceDays, setGraceDays] = useState('7');
  const [updateRequired, setUpdateRequired] = useState(false);
  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [issuedToken, setIssuedToken] = useState<OwnerIssueEnrollmentTokenResponseDto | null>(null);
  const [issuedPin, setIssuedPin] = useState<string | null>(null);
  const [replacing, setReplacing] = useState(false);
  const canReplace = useOwnerCapability('deployment.enrollment');

  const deployment = useQuery({ queryKey: key, queryFn: () => get<OwnerTenantDeploymentDto>(`${base}/deployment`) });
  const d = deployment.data;

  useEffect(() => {
    if (d) {
      setGraceDays(String(d.graceDays));
      setUpdateRequired(d.updateRequired);
      setOfflineEnabled(d.offlineEnabled);
    }
  }, [d]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };
  const fail = (title: string) => (error: unknown) => toast.error(title, describeError(error));

  const save = useMutation({
    mutationFn: () =>
      api<OwnerTenantDeploymentDto>(`${base}/deployment`, {
        method: 'PATCH',
        body: { graceDays: Number.parseInt(graceDays, 10), updateRequired, offlineEnabled, totpCode },
      }),
    onSuccess: () => {
      refresh();
      setTotpCode('');
      toast.success('Deployment settings saved');
    },
    onError: fail('Could not save deployment settings'),
  });

  const issueToken = useMutation({
    mutationFn: () => post<OwnerIssueEnrollmentTokenResponseDto>(`${base}/enrollment-token`, { totpCode }),
    onSuccess: (result) => {
      setIssuedToken(result);
      setTotpCode('');
      refresh();
      toast.success('Activation token issued', 'Copy it now — it is shown only once');
    },
    onError: fail('Could not issue an activation token'),
  });

  const regeneratePin = useMutation({
    mutationFn: () => post<OwnerDownloadPinResponseDto>(`${base}/download-pin`, { totpCode }),
    onSuccess: (result) => {
      setIssuedPin(result.pin);
      setTotpCode('');
      refresh();
      toast.success('Download PIN regenerated', 'The previous PIN no longer works');
    },
    onError: fail('Could not regenerate the download PIN'),
  });

  const retire = useMutation({
    mutationFn: () => post<OwnerTenantDeploymentDto>(`${base}/retire-fingerprint`, { totpCode }),
    onSuccess: () => {
      setTotpCode('');
      refresh();
      toast.success('Activation retired', 'The old machine can no longer sync. Use the licence key or issue a token to activate a replacement');
    },
    onError: fail('Could not retire the fingerprint'),
  });

  const regenerateKey = useMutation({
    mutationFn: () => post<OwnerTenantDeploymentDto>(`${base}/license-key`, { totpCode }),
    onSuccess: () => {
      setTotpCode('');
      refresh();
      toast.success('Licence key replaced', 'The previous key no longer works');
    },
    onError: fail('Could not replace the licence key'),
  });

  if (deployment.isLoading || !d) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Spinner /> Loading deployment…
      </div>
    );
  }

  const totpReady = isValidTotp(totpCode);
  const graceValue = Number.parseInt(graceDays, 10);
  const graceValid = Number.isInteger(graceValue) && graceValue >= 1 && graceValue <= 365;
  const busy = save.isPending || issueToken.isPending || regeneratePin.isPending || retire.isPending || regenerateKey.isPending;
  const lock = LOCK_STATE_BADGE[d.lockState];
  const offlineOff = !d.offlineEnabled;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <HardDrive className="h-4 w-4" /> Deployment — {tenantName}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-3">
          <Row label="Mode">
            <Badge variant={d.deploymentMode === 'LOCAL' ? 'default' : 'secondary'}>
              {d.deploymentMode === 'LOCAL' ? 'Offline (local server)' : 'Online (cloud)'}
            </Badge>
          </Row>
          <Row label="Lock state">
            <Badge variant={lock.variant}>{lock.label}</Badge>
          </Row>
          <Row label="Offline mode">
            <Badge variant={d.offlineEnabled ? 'success' : 'secondary'}>{d.offlineEnabled ? 'Allowed' : 'Not allowed'}</Badge>
          </Row>
          <Row label="Activation">{ACTIVATION_LABEL[d.activationState]}</Row>
          <Row label="Activated on">
            {d.activatedHostname ? <span className="font-mono">{d.activatedHostname}</span> : '—'}
            <span className="block text-xs text-muted-foreground">{d.activatedAt ? dateTime(d.activatedAt) : 'Never'}</span>
          </Row>
          <Row label="Fingerprint">
            <span className="break-all font-mono text-xs text-muted-foreground">{d.hardwareFingerprint ?? '—'}</span>
          </Row>
          <Row label="Running version">
            <span className="font-mono">{d.runningVersion ?? '—'}</span>
          </Row>
          <Row label="Last check-in">{d.lastSeenAt ? dateTime(d.lastSeenAt) : 'Never'}</Row>
          <Row label="Last sync">{d.lastSyncAt ? dateTime(d.lastSyncAt) : 'Never'}</Row>
          <Row label="Days until lock">{d.daysUntilLock === null ? '—' : `${d.daysUntilLock} of ${d.graceDays}`}</Row>
          <Row label="Outbox backlog">
            <span className="font-mono">{d.outboxBacklog ?? '—'}</span>
          </Row>
          <Row label="Disk free">
            <span className="font-mono">{d.diskFreeMb === null ? '—' : `${Math.round(d.diskFreeMb / 1024)} GB`}</span>
          </Row>
          <Row label="Licence delivery">
            {d.deploymentMode === 'LOCAL' ? <LicenceDeliveryPill licence={d.licence} lastCheckInAt={d.lastSeenAt} lockState={d.lockState} /> : 'Applies at once on the cloud'}
          </Row>
          <Row label="Download PIN">{d.hasDownloadPin ? 'Set' : 'Not set'}</Row>
          <Row label="Pending token">{d.pendingToken ? `Valid until ${dateTime(d.pendingToken.expiresAt)}` : 'None'}</Row>
          <div className="col-span-2 md:col-span-3">
            <Row label="Licence key">
              {d.licenseKey ? <span className="select-all break-all font-mono text-xs">{d.licenseKey}</span> : '—'}
              <span className="block text-xs text-muted-foreground">
                Permanent. Give it to the hospital instead of a token: it can activate the same machine again (a reinstall) but a different machine only after you retire this one.
              </span>
            </Row>
          </div>
          {d.lastUpdateFailed && (
            <div className="col-span-2 md:col-span-3">
              <p className="text-[11px] uppercase tracking-wide text-destructive">Last update failed</p>
              <pre className="mt-1 max-h-32 overflow-auto rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs">
                {d.lastUpdateLog ?? 'No log reported'}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Offline mode, grace period and updates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Switch
            checked={offlineEnabled}
            onCheckedChange={setOfflineEnabled}
            label="Allow offline mode"
            description={
              <>
                Off by default. When on, this hospital can be issued an activation token, generate its download PIN and download the
                offline installer. Switching it off later blocks new activations and downloads only — an activated server keeps
                checking in until you retire its fingerprint.
              </>
            }
          />
          <Field label="Grace days before read-only" hint="Days the local install may run without a cloud check-in. Warnings start 3 days before.">
            <Input inputMode="numeric" value={graceDays} onChange={(e) => setGraceDays(e.target.value.replace(/\D/g, '').slice(0, 3))} />
          </Field>
          <Switch
            checked={updateRequired}
            onCheckedChange={setUpdateRequired}
            label="Update required"
            description={
              <>
                When on, this hospital&apos;s install goes read-only if it runs older than the current release&apos;s minimum allowed version.
              </>
            }
          />
          <Button className="w-full" loading={save.isPending} disabled={busy || !totpReady || !graceValid} onClick={() => save.mutate()}>
            Save changes
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-sm">Owner actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {issuedToken && (
            <SecretBox
              label="Offline activation token — shown once"
              value={issuedToken.token}
              hint={`Valid until ${dateTime(issuedToken.expiresAt)}. Enter it in install.ps1 on the hospital's server.`}
            />
          )}
          {issuedPin && <SecretBox label="Download PIN — shown once" value={issuedPin} hint="Used with the hospital's subdomain on the landing page download form." />}
          {d.deploymentMode === 'LOCAL' && (
            <Alert>
              The cloud copy of this hospital is read-only: its staff write on the local server, which mirrors here.
            </Alert>
          )}
          {offlineOff && (
            <Alert variant="warning" className="flex items-center gap-2">
              <WifiOff className="h-4 w-4 flex-none" aria-hidden />
              <span>Offline mode is not allowed for this hospital — switch on &quot;Allow offline mode&quot; and save before issuing an activation token or a download PIN.</span>
            </Alert>
          )}
          <TotpField value={totpCode} onChange={setTotpCode} />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="gap-1.5"
              loading={issueToken.isPending}
              disabled={busy || !totpReady || offlineOff}
              title={offlineOff ? 'Allow offline mode first' : undefined}
              onClick={() => issueToken.mutate()}
            >
              <KeyRound className="h-3.5 w-3.5" /> Issue offline activation token
            </Button>
            <Button
              variant="outline"
              className="gap-1.5"
              loading={regeneratePin.isPending}
              disabled={busy || !totpReady || offlineOff}
              title={offlineOff ? 'Allow offline mode first' : undefined}
              onClick={() => regeneratePin.mutate()}
            >
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate download PIN
            </Button>
            <Button
              variant="outline"
              className="gap-1.5"
              loading={regenerateKey.isPending}
              disabled={busy || !totpReady}
              onClick={() => regenerateKey.mutate()}
            >
              <KeyRound className="h-3.5 w-3.5" /> Replace licence key
            </Button>
            <Button
              variant="destructive"
              className="gap-1.5"
              loading={retire.isPending}
              disabled={busy || !totpReady || !d.hardwareFingerprint}
              onClick={() => retire.mutate()}
            >
              <ShieldOff className="h-3.5 w-3.5" /> Retire fingerprint
            </Button>
            {canReplace && d.deploymentMode === 'LOCAL' && (
              <Button variant="outline" className="gap-1.5" disabled={busy} onClick={() => setReplacing(true)}>
                <HardDrive className="h-3.5 w-3.5" /> Replace server…
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {replacing && <ReplaceServerDialog tenantId={tenantId} tenantName={tenantName} onClose={() => setReplacing(false)} />}
      {d.deploymentMode === 'LOCAL' && <TenantGraceCard tenantId={tenantId} deployment={d} />}
      {d.deploymentMode === 'LOCAL' && <TenantCommandsCard tenantId={tenantId} />}
      {d.deploymentMode === 'LOCAL' && <TenantComparePanel tenantId={tenantId} />}
    </div>
  );
}
